import { doc, runTransaction, serverTimestamp, increment, arrayUnion, arrayRemove } from 'firebase/firestore';
import * as Sentry from "@sentry/react";

export const normalizeTagName = (tagName) => tagName?.trim().toLowerCase() || '';

export const getNormalizedTagDocId = (uid, normalizedName) => `${uid}_${encodeURIComponent(normalizedName)}`;

/**
 * Syncs normalized tags for a document.
 * @param {import('firebase/firestore').Firestore} db
 * @param {string} uid
 * @param {string} documentId
 * @param {'note' | 'insight' | 'concept'} docType
 * @param {string[]} currentTags
 * @param {string[]} previousTags
 */
export const syncNormalizedTags = async (db, uid, documentId, docType, currentTags = [], previousTags = []) => {
  if (!uid || !documentId) return;

  const currentTagsByNormalizedName = new Map();
  (currentTags || []).forEach((tagName) => {
    const normalizedName = normalizeTagName(tagName);
    if (normalizedName && !currentTagsByNormalizedName.has(normalizedName)) {
      currentTagsByNormalizedName.set(normalizedName, tagName.trim());
    }
  });

  const previousTagsByNormalizedName = new Map();
  (previousTags || []).forEach((tagName) => {
    const normalizedName = normalizeTagName(tagName);
    if (normalizedName && !previousTagsByNormalizedName.has(normalizedName)) {
      previousTagsByNormalizedName.set(normalizedName, tagName.trim());
    }
  });

  const tagsToAdd = Array.from(currentTagsByNormalizedName.entries())
    .filter(([normalizedName]) => !previousTagsByNormalizedName.has(normalizedName));
  const tagsToRemove = Array.from(previousTagsByNormalizedName.entries())
    .filter(([normalizedName]) => !currentTagsByNormalizedName.has(normalizedName));

  const docTypeToCountField = {
    note: 'noteCount',
    insight: 'hintCount',
    concept: 'assetCount'
  };
  const countField = docTypeToCountField[docType];

  // 1. Handle Tag Additions
  for (const [normalizedName, tagName] of tagsToAdd) {
    const tagId = getNormalizedTagDocId(uid, normalizedName);
    const tagRef = doc(db, 'tags', tagId);

    try {
      await runTransaction(db, async (transaction) => {
        const tagSnap = await transaction.get(tagRef);
        if (tagSnap.exists()) {
          const data = tagSnap.data();
          const relatedDocumentIds = data.relatedDocumentIds || [];
          if (!relatedDocumentIds.includes(documentId)) {
            const updates = {
              documentCount: increment(1),
              lastUsedAt: serverTimestamp(),
              relatedDocumentIds: arrayUnion(documentId),
              updatedAt: serverTimestamp()
            };
            if (countField) {
              updates[countField] = increment(1);
            }
            transaction.update(tagRef, updates);
          } else {
            // Already counted, just update timestamp
             transaction.update(tagRef, {
              lastUsedAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
          return;
        }

        transaction.set(tagRef, {
          ownerUid: uid,
          name: tagName,
          normalizedName,
          aliases: [],
          documentCount: 1,
          noteCount: docType === 'note' ? 1 : 0,
          hintCount: docType === 'insight' ? 1 : 0,
          assetCount: docType === 'concept' ? 1 : 0,
          lastUsedAt: serverTimestamp(),
          relatedDocumentIds: [documentId],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });
    } catch (error) {
      console.error("Failed to add normalized tag:", tagName, error);
      Sentry.captureException(error, { tags: { action: 'syncNormalizedTags_add' }, extra: { documentId, docType } });
    }
  }

  // 2. Handle Tag Removals
  for (const [normalizedName, tagName] of tagsToRemove) {
    const tagId = getNormalizedTagDocId(uid, normalizedName);
    const tagRef = doc(db, 'tags', tagId);

    try {
      await runTransaction(db, async (transaction) => {
        const tagSnap = await transaction.get(tagRef);
        if (!tagSnap.exists()) return;

        const data = tagSnap.data();
        if (!data.relatedDocumentIds?.includes(documentId)) return;

        const updates = {
          documentCount: increment(-1),
          relatedDocumentIds: arrayRemove(documentId),
          updatedAt: serverTimestamp()
        };
        if (countField) {
          updates[countField] = increment(-1);
        }
        transaction.update(tagRef, updates);
      });
    } catch (error) {
      console.error("Failed to remove normalized tag:", tagName, error);
      Sentry.captureException(error, { tags: { action: 'syncNormalizedTags_remove' }, extra: { documentId, docType } });
    }
  }
};
